import http, { endpoints } from '../http';
import { mapActivityFromDto } from '../activities/helpers';
import { mapCaseContactFromDto, mapCaseDetailsFromDto, mapCaseFromDetailDto, mapCaseFromDto, mapUpdateCaseAddressParamsToDto, } from './helpers';
const efktClient = http.createClient(endpoints.efkt);
export default class CasesApi {
    /**
     * `GET /api/cases`. Free text matches the case display name, the case id as a string, and the
     * linked property's street/city/postalCode — note it does **not** match house number.
     */
    static async searchCases({ search, branchIds, page = 1, limit = 20, sortBy, sortDirection, }) {
        const response = await efktClient.get('/api/cases', {
            params: {
                search: search || undefined,
                branchIds: (branchIds === null || branchIds === void 0 ? void 0 : branchIds.length) ? branchIds.join(',') : undefined,
                page,
                limit,
                sortBy,
                sortDirection,
            },
        });
        return {
            items: response.data.items.map(mapCaseFromDto),
            total: response.data.total,
        };
    }
    /**
     * `GET /api/cases/{caseId}` — the **plural** detail route. A genuine superset of the list row
     * (id/reference/dates/address) plus the owning account (id, name, clientId) and the
     * photographer note, so this is the one to call for a cold load / case detail screen. Returns
     * 404 on an unknown id, which is what the CLAUDE.md cold-load fallback (Efkt first, datahub on
     * 404) needs.
     */
    static async getCaseById(caseId) {
        const response = await efktClient.get(`/api/cases/${encodeURIComponent(caseId)}`);
        return mapCaseFromDetailDto(response.data);
    }
    /**
     * @deprecated Use `getCaseById` — same auth, strictly more fields, and one call instead of two.
     * Kept because `v2/modules/cases/hooks.tsx` still calls it; not for new call sites.
     */
    static async getCaseCompany(caseId) {
        const response = await efktClient.get(`/api/case/${encodeURIComponent(caseId)}`);
        return mapCaseDetailsFromDto(response.data);
    }
    /**
     * @deprecated Use `getCaseById` — one call against the plural route instead of a search-and-scan.
     * Kept because `v2/modules/cases/hooks.tsx` still calls it; not for new call sites.
     */
    static async findCaseById(caseId) {
        const { items } = await CasesApi.searchCases({ search: caseId, limit: 1 });
        return items.find(({ id }) => id === caseId);
    }
    /**
     * EFKT-MOCK — guessed path, no gateway route exists yet. Mirrors datahub's
     * `CasesApi.updateCaseAddress`, same params so `useCaseDetailsPage`'s call site needs no edit.
     */
    static async updateCaseAddress(params) {
        await efktClient.post(`/api/case/${encodeURIComponent(params.caseId)}/address`, mapUpdateCaseAddressParamsToDto(params));
        return { success: true };
    }
    static async getCaseContacts(caseId) {
        const response = await efktClient.get(`/api/cases/${encodeURIComponent(caseId)}/contacts`);
        return response.data.items.map(mapCaseContactFromDto);
    }
    /** `GET /api/cases/{caseId}/activities` — `{items, total}`, newest first. No booqly consumer yet. */
    static async getCaseActivities(caseId, page = 1, limit = 20) {
        const response = await efktClient.get(`/api/cases/${encodeURIComponent(caseId)}/activities`, { params: { page, limit } });
        return {
            items: response.data.items.map(mapActivityFromDto),
            total: response.data.total,
        };
    }
}
